/**
 * BirthdayPopup — full-screen celebration overlay shown once per day
 * when the logged-in user's date_of_birth matches today's date.
 *
 * Features: CSS confetti, floating balloons, cake, user photo, company logo.
 * Dismissed via "Thank You 🎉" button; once dismissed the localStorage key
 * prevents it reappearing until midnight IST.
 */
import React, { useEffect, useRef } from "react";
import useOverlayScrollLock from "@/hooks/useOverlayScrollLock";

/* ── confetti config ───────────────────────────────────────────────────────── */
const CONFETTI_COLORS = [
  "#f59e0b", "#ef4444", "#3b82f6", "#10b981", "#8b5cf6",
  "#f97316", "#ec4899", "#14b8a6", "#f43f5e", "#6366f1",
];
const CONFETTI_COUNT = 60;
const BALLOON_COUNT  = 8;

/* ── helpers ────────────────────────────────────────────────────────────────── */
function rand(min, max) { return Math.random() * (max - min) + min; }
function randInt(min, max) { return Math.floor(rand(min, max + 1)); }

/* ── CSS injected once ──────────────────────────────────────────────────────── */
const STYLE_ID = "flg-birthday-popup-style";
function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes flg-confetti-fall {
      0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
      80%  { opacity: 1; }
      100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
    }
    @keyframes flg-balloon-rise {
      0%   { transform: translateY(0)    rotate(0deg); }
      50%  { transform: translateY(-30px) rotate(6deg);  }
      100% { transform: translateY(-110vh) rotate(-4deg); opacity: 0; }
    }
    @keyframes flg-card-in {
      0%   { transform: scale(0.7) translateY(40px); opacity: 0; }
      70%  { transform: scale(1.03); }
      100% { transform: scale(1) translateY(0); opacity: 1; }
    }
    @keyframes flg-pulse-glow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.5); }
      50%       { box-shadow: 0 0 0 16px rgba(245,158,11,0); }
    }
    .flg-birthday-overlay {
      position: fixed; inset: 0; z-index: 99999;
      background: rgba(10, 6, 40, 0.82);
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
    }
    .flg-birthday-card {
      position: relative; z-index: 2;
      background: #fff; border-radius: 24px;
      padding: 40px 44px 36px;
      max-width: 420px; width: 90%;
      text-align: center;
      animation: flg-card-in 0.55s cubic-bezier(0.22, 1.2, 0.36, 1) both;
      box-shadow: 0 24px 80px rgba(0,0,0,0.35);
    }
    .flg-birthday-avatar {
      width: 88px; height: 88px; border-radius: 50%;
      object-fit: cover;
      border: 4px solid #f59e0b;
      animation: flg-pulse-glow 2s infinite;
      margin: 0 auto 16px;
    }
    .flg-birthday-initial {
      width: 88px; height: 88px; border-radius: 50%;
      background: linear-gradient(135deg, #f59e0b, #ef4444);
      display: flex; align-items: center; justify-content: center;
      font-size: 32px; font-weight: 700; color: #fff;
      margin: 0 auto 16px;
      animation: flg-pulse-glow 2s infinite;
    }
    .flg-birthday-thank-btn {
      margin-top: 24px;
      padding: 13px 36px;
      border: none; border-radius: 50px; cursor: pointer;
      background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
      color: #fff; font-size: 15px; font-weight: 700;
      letter-spacing: 0.02em;
      transition: transform 120ms, box-shadow 120ms;
    }
    .flg-birthday-thank-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(239,68,68,0.4);
    }
  `;
  document.head.appendChild(style);
}

export default function BirthdayPopup({ name, logoUrl, photoUrl, onClose }) {
  const overlayRef = useRef(null);
  useOverlayScrollLock();

  useEffect(() => {
    ensureStyles();
  }, []);

  /* Generate confetti pieces */
  const confetti = Array.from({ length: CONFETTI_COUNT }).map((_, i) => {
    const size    = rand(6, 14);
    const left    = rand(0, 100);
    const delay   = rand(0, 3);
    const dur     = rand(3.5, 7);
    const color   = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    const isRect  = Math.random() > 0.5;
    return (
      <div key={i} style={{
        position: "absolute",
        left: `${left}%`,
        top: `-${rand(10, 40)}px`,
        width:  isRect ? size : size * 0.7,
        height: isRect ? size * 0.4 : size,
        borderRadius: isRect ? 2 : "50%",
        background: color,
        opacity: 0,
        animation: `flg-confetti-fall ${dur}s ${delay}s ease-in infinite`,
        zIndex: 1,
        pointerEvents: "none",
      }} />
    );
  });

  /* Generate balloons */
  const balloons = Array.from({ length: BALLOON_COUNT }).map((_, i) => {
    const emojis = ["🎈", "🎉", "🎊", "🎁"];
    const left   = rand(5, 95);
    const delay  = rand(0, 2);
    const dur    = rand(6, 10);
    const size   = randInt(32, 52);
    return (
      <div key={i} style={{
        position: "absolute",
        bottom: `-${rand(20, 60)}px`,
        left:  `${left}%`,
        fontSize: size,
        animation: `flg-balloon-rise ${dur}s ${delay}s ease-in infinite`,
        zIndex: 1,
        pointerEvents: "none",
        userSelect: "none",
      }}>
        {emojis[i % emojis.length]}
      </div>
    );
  });

  /* Avatar */
  const initials = (name || "?")
    .split(" ")
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "")
    .join("");

  return (
    <div className="flg-birthday-overlay" ref={overlayRef}>
      {confetti}
      {balloons}

      <div className="flg-birthday-card">
        {/* Company logo */}
        {logoUrl && !logoUrl.startsWith("data:") && (
          <img
            src={logoUrl}
            alt="Company"
            style={{ height: 32, maxWidth: 120, objectFit: "contain", marginBottom: 16 }}
          />
        )}

        {/* Cake */}
        <div style={{ fontSize: 52, lineHeight: 1, marginBottom: 12 }}>🎂</div>

        {/* User photo or initials */}
        {photoUrl && !photoUrl.startsWith("data:") ? (
          <img src={photoUrl} alt={name} className="flg-birthday-avatar" />
        ) : (
          <div className="flg-birthday-initial">{initials}</div>
        )}

        {/* Heading */}
        <div style={{
          fontSize: 22, fontWeight: 800, color: "#1a1a2e",
          background: "linear-gradient(135deg, #f59e0b, #ef4444)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          marginBottom: 8,
        }}>
          Happy Birthday, {name}! 🎉
        </div>

        {/* Message */}
        <p style={{
          fontSize: 14, color: "#4b5563", lineHeight: 1.7,
          margin: "0 0 4px",
        }}>
          Wishing you a wonderful year filled with happiness,<br/>
          success and good health.
        </p>
        <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.7, margin: 0 }}>
          Have an amazing birthday! 🎊
        </p>

        {/* CTA */}
        <button className="flg-birthday-thank-btn" onClick={onClose}>
          🎉 Thank You
        </button>
      </div>
    </div>
  );
}
